import axios from 'axios';
import { api } from '../lib/api.js';
import type { FeatureFlags } from '@attendance/shared/schemas/settings';
import type { TerminologyProfile, Vocabulary } from '@attendance/shared/terminology';

const API_ORIGIN = (import.meta.env.VITE_API_URL ?? '').replace(/\/$/, '');

export type BrandingDateFormat = 'DD-MM-YYYY' | 'MM-DD-YYYY' | 'YYYY-MM-DD';
export type BrandingTimeFormat = '12h' | '24h';

export interface PublicTerminology {
  profile: TerminologyProfile;
  /** The word set with the company's own words over it, both languages. */
  vocabulary: Vocabulary;
}

export interface PublicBranding {
  appName: string;
  primaryColor: string;
  logoUrl: string | null;
  /** The sign-in page's photograph, uploaded in Settings → Company → Brand; null = colour only. */
  loginBackgroundUrl?: string | null;
  /**
   * The registered entity — "Al Wathba Building Contracting Co. LLC" beside a
   * trading name of "Al Wathba". Null on a tenant that has not filled Company
   * info in, which is why every reader falls back rather than printing an
   * empty line where a company name should be.
   */
  legalName?: string | null;
  /**
   * Tenant-configured date format pattern. Mirrors
   * `GeneralSettings.dateFormat` and is surfaced publicly so the unauth'd
   * login screen (and `useFormatDate`) can render dates consistently before
   * the user is signed in.
   */
  dateFormat?: BrandingDateFormat;
  /**
   * Tenant-configured clock format ('12h' / '24h'). Surfaced publicly so
   * `useFormatTime` renders times per the General setting instead of the
   * browser locale default.
   */
  timeFormat?: BrandingTimeFormat;
  /**
   * The tenant's IANA zone (`general.timezone`, mirrored on `Company.timezone`)
   * — the zone that decides where one working day ends and the next begins.
   *
   * Surfaced here, on the payload every client already bootstraps, because it
   * is the ONLY basis on which the client's "today" agrees with the server's
   * rollups, and because `/settings/general` is ADMIN/HR-only while the screens
   * that need it (leave request, my-leave) are employee-facing. Read it through
   * `useCompanyTimeZone()` / `useCompanyToday()`, never directly.
   *
   * Optional: an older API build, a failed bootstrap, or a pre-install tenant
   * leaves it absent, and the helpers fall back to the same `Asia/Dubai` the
   * server's `safeTimeZone` does.
   */
  timezone?: string;
  /**
   * The company's ISO currency (`Company.currency`). Money was formatted as AED
   * whatever the company had set (2026-10-02 audit, C-04). Read it through
   * `formatCurrency` / `useCompanyCurrency()`; absent on an older API.
   */
  currency?: string;
  /** The company's words (profile + resolved vocabulary). Absent on legacy/pre-install. */
  terminology?: PublicTerminology;
  /** Enabled feature modules. Absent on legacy/pre-install (treat as all on). */
  features?: FeatureFlags;
  /** The company's password rule, for the change and reset pages (`PasswordRule.tsx`). Absent on an older API. */
  passwordRules?: PublicPasswordRules;
}

/** What varies in the password rule; a capital and a digit are always required. */
export interface PublicPasswordRules {
  minLength: number;
  requireSpecial: boolean;
}

export const brandingApi = {
  /**
   * Public — works without auth and even before install completes
   * (returns null then).
   */
  async getPublic(): Promise<PublicBranding | null> {
    try {
      const { data } = await axios.get<{ data: PublicBranding | null }>(
        `${API_ORIGIN}/api/branding`
      );
      return data.data;
    } catch {
      return null;
    }
  },

  uploadLogo: async (file: File): Promise<{ logoUrl: string }> => {
    const formData = new FormData();
    formData.append('file', file);
    const result = await api.post<{ data: { logoUrl: string } }>(
      '/settings/upload-logo',
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return result.data;
  },

  /** Back to the Hadir logo — `DELETE /settings/logo`. */
  removeLogo: async (): Promise<void> => {
    await api.delete('/settings/logo');
  },

  /** The sign-in page's photograph — `POST /settings/upload-login-background`. */
  uploadLoginBackground: async (file: File): Promise<void> => {
    const formData = new FormData();
    formData.append('file', file);
    await api.post('/settings/upload-login-background', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },

  /** Back to the colour alone — `DELETE /settings/login-background`. */
  removeLoginBackground: async (): Promise<void> => {
    await api.delete('/settings/login-background');
  },
};

/**
 * URL the browser hits to fetch the current logo (if any).
 * Adding a cache-buster on save keeps the new logo visible immediately.
 */
export function logoUrl(version?: string | number): string {
  const base = `${API_ORIGIN}/api/branding/logo`;
  return version ? `${base}?v=${version}` : base;
}

/**
 * URL of the sign-in photograph. The stored path rides along as the cache key,
 * so a replaced photo is fetched at once instead of after the 5-minute cache.
 */
export function loginBackgroundUrl(storedPath: string): string {
  return `${API_ORIGIN}/api/branding/login-background?v=${encodeURIComponent(storedPath)}`;
}
